import { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Search, X, ArrowRight } from "lucide-react";
import { PRODUCTS, formatINR } from "@/lib/products";

const POPULAR = ["Life OS Planner", "Daily Planner", "Weekly Planner", "MBA Planner", "Undated"];

export const SearchDialog = ({ open, onOpenChange }) => {
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const results = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return [];
    return PRODUCTS.filter(
      (p) =>
        p.name.toLowerCase().includes(query) ||
        p.category.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.plannerType?.toLowerCase().includes(query)
    ).slice(0, 8);
  }, [q]);

  const go = (slug) => {
    onOpenChange(false);
    navigate(`/product/${slug}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl top-24 translate-y-0 bg-cream border-line p-0 gap-0 shadow-hover"
        data-testid="search-dialog"
      >
        <DialogTitle className="sr-only">Search products</DialogTitle>
        <div className="flex items-center gap-3 border-b border-line px-5 py-4">
          <Search size={16} className="text-muted_brown" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="search-input"
            placeholder="Search planners..."
            className="flex-1 bg-transparent outline-none text-base text-espresso placeholder:text-muted_brown"
          />
          <button
            onClick={() => onOpenChange(false)}
            data-testid="search-close"
            className="text-muted_brown hover:text-espresso"
            aria-label="Close search"
          >
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {!q && (
            <div className="p-5">
              <div className="overline mb-3">Popular searches</div>
              <div className="flex flex-wrap gap-2">
                {POPULAR.map((t) => (
                  <button
                    key={t}
                    onClick={() => setQ(t)}
                    className="text-xs text-espresso border border-line rounded-full px-3 py-1.5 hover:border-espresso"
                    data-testid={`popular-${t.replace(/ /g, "-").toLowerCase()}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {q && results.length === 0 && (
            <div className="p-8 text-center text-muted_brown text-sm">
              No products match "{q}"
            </div>
          )}

          {results.length > 0 && (
            <ul className="p-2">
              {results.map((p) => (
                <li key={p.id}>
                  <button
                    onClick={() => go(p.slug)}
                    data-testid={`search-result-${p.slug}`}
                    className="w-full flex items-center gap-4 p-3 rounded-lg hover:bg-ivory transition-colors text-left"
                  >
                    <img
                      src={p.images[0]}
                      alt=""
                      className="w-14 h-14 rounded-lg object-cover border border-line"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-espresso text-sm">{p.name}</div>
                      <div className="text-xs text-muted_brown capitalize">{p.category}</div>
                    </div>
                    <div className="text-sm text-espresso whitespace-nowrap">
                      {formatINR(p.priceINR)}
                    </div>
                    <ArrowRight size={14} className="text-muted_brown" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="border-t border-line p-4 flex items-center justify-between">
            <Link
              to="/shop"
              onClick={() => onOpenChange(false)}
              className="text-xs uppercase tracking-[0.15em] text-espresso hover:underline"
              data-testid="search-view-all"
            >
              View all products →
            </Link>
            <div className="text-[10px] text-muted_brown">Press Esc to close</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
